import { type MouseEvent } from 'react'
import cn from 'clsx'
import { Link } from '@muse-web/router'

import { IMAGE_RESIZE_SPEC } from '@core/image'
import { Cover } from '@ds/components'

import type { TAudioCardProps } from './AudioCard.types'
import { AudioCardZone } from './AudioCardZone'
import styles from './AudioCard.module.css'

const COVER_SIZES = '(max-width: 767px) 56px, 140px'

const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation()
}

export const AudioCard = ({
    audioId,
    variant = 'default',
    coverSrc,
    coverAlt,
    title,
    titleHref,
    onTitleClick,
    authorLabel,
    authorHref,
    onAuthorClick,
    coverPlaySlot,
    bodySlot,
    statsSlot,
    tagsSlot,
    timeSlot,
    actionsSlot,
    followSlot,
    followMobileSlot,
    isTabletUp,
    onCardClick,
}: TAudioCardProps) => {
    const isCompact = variant === 'compact'
    const stopCardClick = onCardClick ? stopPropagation : undefined
    const cardClassName = cn(styles.card, {
        [styles.compact]: isCompact,
        [styles.clickable]: Boolean(onCardClick),
    })

    const handleTitleClick = (event: MouseEvent) => {
        stopCardClick?.(event)
        onTitleClick()
    }

    const handleAuthorClick = (event: MouseEvent) => {
        stopCardClick?.(event)
        onAuthorClick()
    }

    const content = (
        <>
            <div className={styles.top}>
                <div className={styles.coverBox}>
                    <Cover
                        src={coverSrc}
                        alt={coverAlt}
                        sizes={COVER_SIZES}
                        className={styles.coverImage}
                        resize={IMAGE_RESIZE_SPEC}
                    />
                    {coverPlaySlot ? (
                        <AudioCardZone className={styles.coverPlay} onClick={stopCardClick}>
                            {coverPlaySlot}
                        </AudioCardZone>
                    ) : null}
                </div>

                <div className={styles.main}>
                    <div className={styles.titleRow}>
                        <div className={styles.titleCol}>
                            <Link
                                href={titleHref}
                                onClick={handleTitleClick}
                                data-test="audio-link"
                                className={styles.title}
                            >
                                {title}
                            </Link>
                            <div className={styles.authorRow}>
                                <Link
                                    href={authorHref}
                                    onClick={handleAuthorClick}
                                    className={styles.author}
                                >
                                    {authorLabel}
                                </Link>
                                {followSlot ? (
                                    <AudioCardZone
                                        className={styles.follow}
                                        onClick={stopCardClick}
                                    >
                                        {followSlot}
                                    </AudioCardZone>
                                ) : null}
                            </div>
                        </div>

                        {!isCompact && isTabletUp ? (
                            <div className={styles.statsActions}>
                                {statsSlot}
                                <AudioCardZone className={styles.actions} onClick={stopCardClick}>
                                    {followMobileSlot ? (
                                        <div className={styles.followMobile}>
                                            {followMobileSlot}
                                        </div>
                                    ) : null}
                                    {actionsSlot}
                                </AudioCardZone>
                            </div>
                        ) : null}
                    </div>

                    {!isCompact && isTabletUp && bodySlot ? (
                        <AudioCardZone className={styles.body} onClick={stopCardClick}>
                            {bodySlot}
                        </AudioCardZone>
                    ) : null}

                    {!isCompact ? (
                        <div className={styles.bottomRow}>
                            {tagsSlot ? (
                                <AudioCardZone className={styles.tags} onClick={stopCardClick}>
                                    {tagsSlot}
                                </AudioCardZone>
                            ) : null}
                            {isTabletUp && timeSlot ? (
                                <AudioCardZone className={styles.time} onClick={stopCardClick}>
                                    {timeSlot}
                                </AudioCardZone>
                            ) : null}
                        </div>
                    ) : null}

                    {isCompact ? (
                        <AudioCardZone className={styles.mobileActions} onClick={stopCardClick}>
                            {statsSlot}
                            {actionsSlot}
                        </AudioCardZone>
                    ) : null}
                </div>
            </div>

            {!isCompact && !isTabletUp ? (
                <div className={styles.mobileBottom}>
                    <span className={styles.stats}>{statsSlot}</span>
                    <AudioCardZone className={styles.mobileActions} onClick={stopCardClick}>
                        {followMobileSlot ? (
                            <div className={styles.followMobile}>{followMobileSlot}</div>
                        ) : null}
                        {actionsSlot}
                    </AudioCardZone>
                </div>
            ) : null}
        </>
    )

    return onCardClick ? (
        <div
            data-test="author-audio-card"
            data-test-audio-id={audioId}
            className={cardClassName}
            role="presentation"
            onClick={onCardClick}
        >
            {content}
        </div>
    ) : (
        <div data-test="author-audio-card" data-test-audio-id={audioId} className={cardClassName}>
            {content}
        </div>
    )
}
